<template>
    <v-card>
        <v-card-title>
            <v-row align="center">
                <v-col class="grow ma-2"> Readout Groups </v-col>
                <v-col class="shrink text-right">
                    <v-btn color="primary" class="ma-2" @click="showCreateReadoutGroupForm">
                        New Readout Group
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :headers="readoutGroupHeaders"
            :items="readoutGroups"
            no-results-text="No readout groups."
            item-key="id"
            density="compact"
            class="elevation-1"
            :items-per-page="25"
        >
            <template v-slot:item.readouts="{ item }">
                <v-row>
                    <v-col>
                        <b>{{ getThingSchemaDisplayName(item.thingSchemaId) }}</b> ({{
                            getThingSchemaName(item.thingSchemaId)
                        }})
                        <br />
                        {{ item.requestedAttributes.map((attr) => attr.attributeName).join(", ") }}
                        <br />
                        {{ item.filter }}
                    </v-col>
                </v-row>
            </template>
            <template v-slot:item.isProtocolReadoutGroup="{ item }">
                <v-icon>{{ isProtocolReadoutGroup(item) ? "$check" : "" }}</v-icon>
            </template>
            <template v-slot:item.edit="{ item }">
                <v-icon v-if="!isReadonly(item)" @click="showEditReadoutGroup(item)"
                >$pencil</v-icon
                >
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon v-if="!isReadonly(item)" @click="deleteReadoutGroup(item)">$delete</v-icon>
            </template>
        </v-data-table>
        <v-dialog v-model="showReadoutGroupForm" width="1000" persistent>
            <readout-group-form
                :abstractSchemaConfig="abstractSchemaConfig"
                :experiment="experiment"
                :readout-group="selectedReadoutGroup"
                @closed="readoutGroupFormClosed"
                @create="createReadoutGroup"
                @update="updateReadoutGroup"
            />
        </v-dialog>
    </v-card>
</template>
<script setup lang="ts">
import { ref } from "vue";
import { VDataTableHeader } from "@/vuetifyTypeFixes";
import { AbstractSchemaConfig } from "@/services/schemas/manageSchemasAPI";
import { ReadoutGroup, SavedReadoutGroup } from "@/services/readoutGroups/readoutGroupsAPI";
import { Experiment, ThingSchema } from "@/services/things/models";
import { findThingSchema } from "@/lib/thingUtils";
import _ from "lodash";
import ReadoutGroupForm from "@/components/Measurements/ReadoutGroupForm.vue";

const { readoutGroups, abstractSchemaConfig, experiment } = defineProps<{
    readoutGroups: SavedReadoutGroup[];
    abstractSchemaConfig: AbstractSchemaConfig;
    experiment?: Experiment;
}>();

const emit = defineEmits<{
    (e: "create", item: ReadoutGroup): void;
    (e: "update", item: SavedReadoutGroup): void;
    (e: "delete", item: SavedReadoutGroup): void;
}>();

const selectedReadoutGroup = ref<SavedReadoutGroup | null>(null);
const showReadoutGroupForm = ref(false);

const readoutGroupHeaders = ref<VDataTableHeader[]>([
    {
        title: "Name",
        value: "name",
        align: "start",
        sortable: false,
        width: "20%",
    },
    {
        title: "Description",
        value: "description",
        align: "start",
        sortable: false,
        width: "35%",
    },
    {
        title: "Readouts",
        value: "readouts",
        sortable: false,
        align: "start",
        width: "20%",
    },
    {
        title: "Filter on Entry Relation Attribute",
        value: "filterEntryRelationAttribute",
        sortable: false,
        align: "start",
        width: "40%",
    },
    {
        title: "Schema Configuration Readout Group",
        value: "isProtocolReadoutGroup",
        sortable: false,
        align: "start",
        width: "10%",
    },
    {
        title: "Edit",
        value: "edit",
        sortable: false,
        align: "start",
        width: "5%",
    },
    {
        title: "Delete",
        value: "delete",
        sortable: false,
        align: "start",
        width: "5%",
    },
]);

function isExperimentReadoutGroupList(): boolean {
    return !_.isNil(experiment);
}

function isReadonly(readoutGroup: ReadoutGroup): boolean {
    return isExperimentReadoutGroupList() && isProtocolReadoutGroup(readoutGroup);
}

function isProtocolReadoutGroup(readoutGroup: ReadoutGroup): boolean {
    return _.isNil(readoutGroup.experimentCode);
}

function getThingSchemaDisplayName(thingSchemaId: string): string {
    const thingSchema = getThingSchema(thingSchemaId);
    if (thingSchema) {
        return thingSchema.displayName;
    } else {
        return `${thingSchemaId} not found`;
    }
}

function getThingSchemaName(thingSchemaId: string): string {
    const thingSchema = getThingSchema(thingSchemaId);
    if (thingSchema) {
        return thingSchema.name;
    } else {
        return `${thingSchemaId} not found`;
    }
}

function getThingSchema(thingSchemaId: string): ThingSchema | undefined {
    return findThingSchema(thingSchemaId, abstractSchemaConfig);
}

function showCreateReadoutGroupForm() {
    selectedReadoutGroup.value = null;
    showReadoutGroupForm.value = true;
}

function showEditReadoutGroup(readoutGroup: SavedReadoutGroup): void {
    selectedReadoutGroup.value = readoutGroup;
    showReadoutGroupForm.value = true;
}

function readoutGroupFormClosed(): void {
    resetReadoutGroupForm();
}

function createReadoutGroup(readoutGroup: ReadoutGroup): void {
    emit("create", readoutGroup);
    resetReadoutGroupForm();
}

function updateReadoutGroup(readoutGroup: SavedReadoutGroup): void {
    emit("update", readoutGroup);
    resetReadoutGroupForm();
}

function resetReadoutGroupForm(): void{
    selectedReadoutGroup.value = null;
    showReadoutGroupForm.value = false;
}

async function deleteReadoutGroup(readoutGroup: SavedReadoutGroup): Promise<void> {
    if (confirm("Are you sure you want to delete this readout group?")) {
        emit("delete", readoutGroup);
    }
}

</script>
